import { c as _c } from "react/compiler-runtime";
import { patchOrgPlaybooksByIdMutation } from '@onefootprint/fetch-dashboard';
import { IcoChevronDown16, IcoChevronRight16, IcoCopy16, IcoDotSmall16, IcoFileSearch16, IcoFileSearchAlt16, IcoLayer0116, type Icon, IcoOff16, IcoPencil16 } from '@onefootprint/icons';
import type { PlaybookVersionNoGraph } from '@onefootprint/request-types/dashboard';
import { Dropdown, Shimmer, Tooltip, useConfirmationDialog } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { cx } from 'class-variance-authority';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PLAYBOOK_KEYS_Q, PLAYBOOK_VERSION_Q } from '@/components/entities';
import { useStore } from '@/components/playbook-builder';
import EmployeeGraphActions from '@/components/playbook-builder/components/employee-graph-actions';
import useSession from '@/hooks/use-session';
import { type KeyEnvironment, useCopyPublishableKey } from '../../hooks/use-copy-publishable-key';
import { usePlaybook } from '../../hooks/use-playbook';
import DuplicateDialog from '../duplicate-dialog/duplicate-dialog';
import RenameDialog from '../rename-dialog/rename-dialog';
const ENVIRONMENTS: KeyEnvironment[] = ['sandbox', 'production'];
const EnvironmentSubmenu = t0 => {
  const $ = _c(11);
  const {
    icon,
    trigger,
    label,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== icon || $[1] !== trigger) {
    t1 = <Dropdown.SubTrigger iconLeft={icon} iconRight={IcoChevronRight16}>{trigger}</Dropdown.SubTrigger>;
    $[0] = icon;
    $[1] = trigger;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== label || $[4] !== onSelect) {
    t2 = ENVIRONMENTS.map(environment => <Dropdown.Item key={environment} onSelect={() => onSelect(environment)}>{label(environment)}</Dropdown.Item>);
    $[3] = label;
    $[4] = onSelect;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <Dropdown.Portal><Dropdown.SubContent sideOffset={4}><Dropdown.Group>{t2}</Dropdown.Group></Dropdown.SubContent></Dropdown.Portal>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t3) {
    t4 = <Dropdown.Sub>{t1}{t3}</Dropdown.Sub>;
    $[8] = t1;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
type ActionsDropdownProps = {
  isDraft: true;
  name: string;
} | {
  isDraft?: false;
  playbook: {
    id: string;
    name: string;
    hasProdOnboarding: boolean;
  };
  publishedVersion: PlaybookVersionNoGraph;
  canEdit: boolean;
};

/**
 * The playbook editor's name + actions menu, shared by the committed and draft editors. A draft has no
 * committed playbook, so it offers only the graph actions (firm employees) or a plain name; the committed
 * variant adds rename/duplicate/key/onboardings/disable and mounts the hooks those need.
 */
const ActionsDropdown = props => {
  const $ = _c(6);
  if (props.isDraft) {
    let t0;
    if ($[0] !== props.name) {
      t0 = <DraftActionsMenu name={props.name} />;
      $[0] = props.name;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] !== props.canEdit || $[3] !== props.playbook || $[4] !== props.publishedVersion) {
    t0 = <CommittedActionsMenu playbook={props.playbook} publishedVersion={props.publishedVersion} canEdit={props.canEdit} />;
    $[2] = props.canEdit;
    $[3] = props.playbook;
    $[4] = props.publishedVersion;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  return t0;
};
const DraftActionsMenu = t0 => {
  const $ = _c(15);
  const {
    name
  } = t0;
  const {
    data: t1
  } = useSession();
  const {
    user
  } = t1;
  const [open, setOpen] = useState(false);
  if (!user?.isFirmEmployee) {
    let t2;
    if ($[0] !== name) {
      t2 = <span className="truncate text-label-2 text-primary">{name}</span>;
      $[0] = name;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  const t2 = !open;
  let t3;
  if ($[2] !== open || $[3] !== t2) {
    t3 = cx("flex items-center gap-1 rounded-(--button-border-radius) h-7 pl-2 pr-1 min-w-0 text-label-2 text-primary cursor-pointer transition-colors", {
      "bg-senary/70": open,
      "bg-primary hover:bg-hover": t2
    });
    $[2] = open;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== name) {
    t4 = <span className="truncate">{name}</span>;
    $[5] = name;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <IcoChevronDown16 color="tertiary" />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3 || $[9] !== t4) {
    t6 = <Dropdown.Trigger asChild={true}><button type="button" className={t3}>{t4}{t5}</button></Dropdown.Trigger>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Dropdown.Portal><Dropdown.Content align="start"><EmployeeGraphActions /></Dropdown.Content></Dropdown.Portal>;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== open || $[13] !== t6) {
    t8 = <Dropdown.Root open={open} onOpenChange={setOpen}>{t6}{t7}</Dropdown.Root>;
    $[12] = open;
    $[13] = t6;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
};
type CommittedActionsMenuProps = {
  playbook: {
    id: string;
    name: string;
    hasProdOnboarding: boolean;
  };
  publishedVersion: PlaybookVersionNoGraph;
  canEdit: boolean;
};
const CommittedActionsMenu = t0 => {
  const $ = _c(110);
  const {
    playbook,
    publishedVersion,
    canEdit
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "header.leftHeaderActions"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const confirm = useConfirmationDialog();
  const [open, setOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = patchOrgPlaybooksByIdMutation();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const updatePlaybook = useMutation(t2);
  const {
    selectedVersionNoGraph: selectedVersion
  } = usePlaybook(publishedVersion.playbookId);
  const {
    data: t3
  } = useStore();
  const {
    graph
  } = t3;
  const router = useRouter();
  const {
    data: t4,
    isLive,
    setIsLive
  } = useSession();
  const {
    user
  } = t4;
  const publishableKey = useCopyPublishableKey(publishedVersion.key);
  let t5;
  if ($[2] !== publishableKey) {
    t5 = environment => {
      setOpen(false);
      publishableKey.copy(environment);
    };
    $[2] = publishableKey;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const handleCopyPublishableKey = t5;
  let t6;
  if ($[4] !== graph.subject || $[5] !== isLive || $[6] !== router || $[7] !== setIsLive) {
    t6 = async (environment_0, query) => {
      setOpen(false);
      const nextIsLive = environment_0 === "production";
      const list = graph.subject === "business" ? "businesses" : "users";
      await router.push(`/${list}?${query}`);
      if (nextIsLive !== isLive) {
        await setIsLive(nextIsLive);
      }
    };
    $[4] = graph.subject;
    $[5] = isLive;
    $[6] = router;
    $[7] = setIsLive;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const handleViewOnboardings = t6;
  let t7;
  if ($[9] !== handleViewOnboardings || $[10] !== publishableKey || $[11] !== selectedVersion?.version) {
    t7 = environment_1 => {
      const key = publishableKey.keyFor(environment_1);
      if (!key) {
        return;
      }
      handleViewOnboardings(environment_1, `${PLAYBOOK_VERSION_Q}=${key}:v${selectedVersion?.version}`);
    };
    $[9] = handleViewOnboardings;
    $[10] = publishableKey;
    $[11] = selectedVersion?.version;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handleViewVersionOnboardings = t7;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setOpen(false);
      setRenameDialogOpen(true);
    };
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const handleRename = t8;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setOpen(false);
      setDuplicateDialogOpen(true);
    };
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const handleDuplicate = t9;
  let t10;
  if ($[15] !== confirm || $[16] !== playbook.id || $[17] !== router || $[18] !== t || $[19] !== updatePlaybook) {
    t10 = async () => {
      const action = async () => {
        ;
        try {
          await updatePlaybook.mutateAsync({
            body: {
              status: "disabled"
            },
            path: {
              id: playbook.id
            }
          });
          router.replace("/playbooks");
        } catch (t11) {
          const error = t11;
          console.error(error);
        }
      };
      confirm.open({
        title: t(_temp),
        description: t(_temp2),
        primaryButton: {
          label: t(_temp3),
          onClick: action
        },
        secondaryButton: {
          label: t(_temp4)
        }
      });
    };
    $[15] = confirm;
    $[16] = playbook.id;
    $[17] = router;
    $[18] = t;
    $[19] = updatePlaybook;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const handleDisable = t10;
  let t11;
  if ($[21] !== t) {
    t11 = t(_temp5);
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const t12 = !open;
  let t13;
  if ($[23] !== open || $[24] !== t12) {
    t13 = cx("flex items-center gap-2 rounded-(--button-border-radius) h-7 px-2 py-2 text-label-2 cursor-pointer transition-colors min-w-0 md:min-w-auto", {
      "bg-primary hover:bg-hover": t12,
      "bg-senary/70": open
    });
    $[23] = open;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== playbook.name) {
    t14 = playbook.name ? playbook.name : <Shimmer className="w-12 h-4" />;
    $[26] = playbook.name;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t14) {
    t15 = <div className="truncate max-w-48">{t14}</div>;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== playbook.hasProdOnboarding || $[31] !== t) {
    t16 = playbook.hasProdOnboarding && <Tooltip content={t(_temp6)} side="right"><IcoDotSmall16 color="success" /></Tooltip>;
    $[30] = playbook.hasProdOnboarding;
    $[31] = t;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t15 || $[34] !== t16) {
    t17 = <div className="flex items-center gap-0.5 min-w-0 md:min-w-auto">{t15}{t16}</div>;
    $[33] = t15;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <IcoChevronDown16 color="tertiary" />;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t11 || $[38] !== t13 || $[39] !== t17) {
    t19 = <Dropdown.Trigger asChild={true}><button aria-label={t11} type="button" className={t13}>{t17}{t18}</button></Dropdown.Trigger>;
    $[37] = t11;
    $[38] = t13;
    $[39] = t17;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== canEdit || $[42] !== t) {
    t20 = canEdit ? <Dropdown.Item iconLeft={IcoPencil16} onSelect={handleRename}>{t(_temp7)}</Dropdown.Item> : null;
    $[41] = canEdit;
    $[42] = t;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== canEdit || $[45] !== t) {
    t21 = canEdit ? <Dropdown.Item iconLeft={IcoLayer0116} onSelect={handleDuplicate}>{t(_temp8)}</Dropdown.Item> : null;
    $[44] = canEdit;
    $[45] = t;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t) {
    t22 = t(_temp9);
    $[47] = t;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t) {
    t23 = environment_2 => t($_8 => $_8.copyPublishableKey[environment_2]);
    $[49] = t;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== handleCopyPublishableKey || $[52] !== t22 || $[53] !== t23) {
    t24 = <EnvironmentSubmenu icon={IcoCopy16} trigger={t22} label={t23} onSelect={handleCopyPublishableKey} />;
    $[51] = handleCopyPublishableKey;
    $[52] = t22;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  const t25 = selectedVersion?.version;
  let t26;
  if ($[55] !== t || $[56] !== t25) {
    t26 = t(_temp10, {
      versionNumber: t25
    });
    $[55] = t;
    $[56] = t25;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== selectedVersion?.version || $[59] !== t) {
    t27 = environment_3 => t($_10 => $_10.viewOnboardingsVersion[environment_3], {
      versionNumber: selectedVersion?.version
    });
    $[58] = selectedVersion?.version;
    $[59] = t;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== handleViewVersionOnboardings || $[62] !== t26 || $[63] !== t27) {
    t28 = <EnvironmentSubmenu icon={IcoFileSearch16} trigger={t26} label={t27} onSelect={handleViewVersionOnboardings} />;
    $[61] = handleViewVersionOnboardings;
    $[62] = t26;
    $[63] = t27;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== t) {
    t29 = t(_temp11);
    $[65] = t;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== t) {
    t30 = environment_4 => t($_12 => $_12.viewOnboardings[environment_4]);
    $[67] = t;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== handleViewOnboardings || $[70] !== publishedVersion.playbookId) {
    t31 = environment_5 => handleViewOnboardings(environment_5, `${PLAYBOOK_KEYS_Q}=${publishedVersion.playbookId}`);
    $[69] = handleViewOnboardings;
    $[70] = publishedVersion.playbookId;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== t29 || $[73] !== t30 || $[74] !== t31) {
    t32 = <EnvironmentSubmenu icon={IcoFileSearchAlt16} trigger={t29} label={t30} onSelect={t31} />;
    $[72] = t29;
    $[73] = t30;
    $[74] = t31;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  let t33;
  if ($[76] !== t20 || $[77] !== t21 || $[78] !== t24 || $[79] !== t28 || $[80] !== t32) {
    t33 = <Dropdown.Group>{t20}{t21}{t24}{t28}{t32}</Dropdown.Group>;
    $[76] = t20;
    $[77] = t21;
    $[78] = t24;
    $[79] = t28;
    $[80] = t32;
    $[81] = t33;
  } else {
    t33 = $[81];
  }
  let t34;
  if ($[82] !== user?.isFirmEmployee) {
    t34 = user?.isFirmEmployee ? <><Dropdown.Divider /><EmployeeGraphActions /></> : null;
    $[82] = user?.isFirmEmployee;
    $[83] = t34;
  } else {
    t34 = $[83];
  }
  let t35;
  if ($[84] !== canEdit || $[85] !== handleDisable || $[86] !== t) {
    t35 = canEdit ? <><Dropdown.Divider /><Dropdown.Group><Dropdown.Item iconLeft={IcoOff16} destructive={true} onSelect={handleDisable}>{t(_temp12)}</Dropdown.Item></Dropdown.Group></> : null;
    $[84] = canEdit;
    $[85] = handleDisable;
    $[86] = t;
    $[87] = t35;
  } else {
    t35 = $[87];
  }
  let t36;
  if ($[88] !== t33 || $[89] !== t34 || $[90] !== t35) {
    t36 = <Dropdown.Portal><Dropdown.Content align="start">{t33}{t34}{t35}</Dropdown.Content></Dropdown.Portal>;
    $[88] = t33;
    $[89] = t34;
    $[90] = t35;
    $[91] = t36;
  } else {
    t36 = $[91];
  }
  let t37;
  if ($[92] !== open || $[93] !== t19 || $[94] !== t36) {
    t37 = <Dropdown.Root open={open} onOpenChange={setOpen}>{t19}{t36}</Dropdown.Root>;
    $[92] = open;
    $[93] = t19;
    $[94] = t36;
    $[95] = t37;
  } else {
    t37 = $[95];
  }
  let t38;
  if ($[96] !== playbook.id || $[97] !== playbook.name) {
    t38 = {
      id: playbook.id,
      name: playbook.name
    };
    $[96] = playbook.id;
    $[97] = playbook.name;
    $[98] = t38;
  } else {
    t38 = $[98];
  }
  let t39;
  if ($[99] !== duplicateDialogOpen || $[100] !== publishedVersion || $[101] !== t38) {
    t39 = <DuplicateDialog playbook={t38} publishedVersion={publishedVersion} open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen} />;
    $[99] = duplicateDialogOpen;
    $[100] = publishedVersion;
    $[101] = t38;
    $[102] = t39;
  } else {
    t39 = $[102];
  }
  let t40;
  if ($[103] !== playbook || $[104] !== renameDialogOpen) {
    t40 = <RenameDialog playbook={playbook} open={renameDialogOpen} onOpenChange={setRenameDialogOpen} />;
    $[103] = playbook;
    $[104] = renameDialogOpen;
    $[105] = t40;
  } else {
    t40 = $[105];
  }
  let t41;
  if ($[106] !== t37 || $[107] !== t39 || $[108] !== t40) {
    t41 = <>{t37}{t39}{t40}</>;
    $[106] = t37;
    $[107] = t39;
    $[108] = t40;
    $[109] = t41;
  } else {
    t41 = $[109];
  }
  return t41;
};
export default ActionsDropdown;
function _temp($) {
  return $.disablePlaybook.confirmation.title;
}
function _temp2($_0) {
  return $_0.disablePlaybook.confirmation.description;
}
function _temp3($_1) {
  return $_1.disablePlaybook.confirmation.confirm;
}
function _temp4($_2) {
  return $_2.disablePlaybook.confirmation.cancel;
}
function _temp5($_3) {
  return $_3.triggerLabel;
}
function _temp6($_4) {
  return $_4.production;
}
function _temp7($_5) {
  return $_5.rename;
}
function _temp8($_6) {
  return $_6.duplicate;
}
function _temp9($_7) {
  return $_7.copyPublishableKey.trigger;
}
function _temp10($_9) {
  return $_9.viewOnboardingsVersion.trigger;
}
function _temp11($_11) {
  return $_11.viewOnboardings.trigger;
}
function _temp12($_13) {
  return $_13.disablePlaybook.trigger;
}